import { Component, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, Params, Router, RouterModule } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../core/utils";
import { noWhitespaceValidator } from "../../../core/validators";
import { AuthLayoutComponent, FormInputComponent } from "../../../shared/components";
import { Constants } from "../../../shared/constants/constants";
import { Language, VerificationMethod } from "../../../shared/constants/enums";
import {
  LoginResponse,
  MainResponseModel,
  NewPassword,
  SecurityQuestion,
  SecurityQuestionAnswerModel,
  VerificationRequestSms,
  VerificationRequestSubmit,
} from "../../../shared/models";
import { AppService, LoginService } from "../../../shared/services";

interface ResetPasswordOption {
  value: VerificationMethod;
  text: string;
  icon: string;
}

@Component({
  selector: "adaa-new-password",
  imports: [TranslatePipe, ReactiveFormsModule, RouterModule, AuthLayoutComponent, FormInputComponent],
  templateUrl: "./new-password.html",
  styleUrl: "./new-password.scss",
})
export class NewPasswordComponent implements OnInit {
  private _formBuilder = inject(FormBuilder);
  private _activatedRoute = inject(ActivatedRoute);
  private _router = inject(Router);
  private _loginService = inject(LoginService);
  private _appService = inject(AppService);
  private _translateService = inject(TranslateService);
  private _toastrService = inject(ToastrService);
  private _destroyRef = inject(DestroyRef);

  private _token: string;
  private readonly _lockDurationInSeconds = 60;
  private _lockIntervalId: ReturnType<typeof setInterval> | undefined;

  verificationMethod = VerificationMethod;
  AdaaHelper = AdaaHelper;
  readonly newPasswordForm = signal<FormGroup | undefined>(undefined);
  readonly user = signal<LoginResponse | undefined>(undefined);
  readonly resetPasswordOptions = signal<ResetPasswordOption[]>([]);
  readonly questions = signal<SecurityQuestion[]>([]);
  readonly currentStep = signal<number>(1);
  readonly isUserDefined = signal<boolean>(true);
  readonly errorMessage = signal<string | undefined>(undefined);
  readonly showPassword = signal<boolean>(false);
  readonly showConfirmPassword = signal<boolean>(false);
  readonly lockCounter = signal(0);

  constructor() {
    this._destroyRef.onDestroy(() => this._clearLockInterval());
  }

  public get verificationMethodValue(): string {
    return this.newPasswordForm()?.get("verificationMethod")?.value;
  }

  public ngOnInit(): void {
    this._prepareForm();

    this._activatedRoute.params.subscribe({
      next: (params: Params) => {
        this._token = params["token"];
        this._getSecurityQuestions();
      },
    });
  }

  public sendVerificationCode(): void {
    const user = this.user();

    if (!AdaaHelper.isDefined(user)) {
      this._markUserAsUndefined();
      return;
    }

    const model: VerificationRequestSms = {
      language: this._appService.language(),
      text: "code",
      userId: user.id,
    };

    this._loginService.fetchVerificationCode(model, VerificationMethod.SMS).subscribe((response: MainResponseModel) => {
      if (response.responseCode === "1") {
        this._lockVerificationButton();
      }
    });
  }

  public resetPasswordOptionsUpdated(value: VerificationMethod): void {
    this.newPasswordForm()?.get("verificationMethod")?.setValue(value);
  }

  /**
   * @description 1ed step validate the account new password
   */
  public validateAccount(): void {
    const form = this.newPasswordForm();

    if (this.verificationMethodValue === VerificationMethod.QUESTIONS) {
      const firstQuestion = form?.get("firstQuestion")?.value;
      const secondQuestion = form?.get("secondQuestion")?.value;
      if (firstQuestion && secondQuestion) {
        this._loginService
          .validateQuestions(this._token, this._getDataValidateByQuestions(firstQuestion, secondQuestion))
          .subscribe((response: MainResponseModel) => {
            response.responseCode === "1"
              ? this.currentStep.set(2)
              : this._toastrService.error(this._translateService.instant("login.error_answers"));
          });
      } else this._toastrService.error(this._translateService.instant("login.error_answers"));
    } else {
      const code = form?.get("verificationCode")?.value;
      if (code) {
        this._loginService
          .validateCode(this._token, this._getDataValidateByCode(code))
          .subscribe((response: MainResponseModel) => {
            response.responseCode === "1"
              ? this.currentStep.set(2)
              : this._toastrService.error(this._translateService.instant("login.error_code"));
          });
      } else this._toastrService.error(this._translateService.instant("login.error_code"));
    }
  }

  /**
   * @description 2ed step validate the account new password
   */
  public validatePassword(): void {
    const form = this.newPasswordForm();
    const password = form?.get("password")?.value;
    const confirmPassword = form?.get("confirmPassword")?.value;

    if (password && password === confirmPassword) {
      this._loginService
        .updatePassword(this._token, this._getDataPassword(password))
        .subscribe((response: MainResponseModel) => {
          if (+response.responseCode === Constants.CONSTANT_ERROR_CODE.PASSWORD_WRONG_COMPLEXITY) {
            this._toastrService.error(
              this._translateService.instant("notification.error.password_requirements_failed")
            );
          } else if (+response.responseCode === Constants.CONSTANT_ERROR_CODE.AD_USER_NOT_EXISTS) {
            this._toastrService.error(this._translateService.instant("notification.error.ad_user_not_exists"));
          } else if (+response.responseCode === 1) {
            this._router.navigateByUrl("/login");
          }
        });
    } else this._toastrService.error(this._translateService.instant("login.password_incorrect"));
  }

  /**
   * @description To prepare the new password form
   * @private
   */
  private _prepareForm(): void {
    const form = this._formBuilder.group({
      verificationMethod: [VerificationMethod.SMS],
      verificationCode: [null, Validators.required],
      firstQuestion: [null, Validators.required],
      secondQuestion: [null, Validators.required],
      password: [null, [Validators.required, noWhitespaceValidator()]],
      confirmPassword: [null, [Validators.required, noWhitespaceValidator()]],
    });

    this.newPasswordForm.set(form);
  }

  /**
   * @description
   * @private
   */
  private _getUserByToken(isQuestionsAnswered: boolean = true): void {
    this._loginService.getUserbyToken(this._token).subscribe({
      next: (response: MainResponseModel) => {
        if (response.inError)
          this.errorMessage.set(
            response.responseCode === "39" || response.responseCode === "38"
              ? `notification.error.code_${response.responseCode}`
              : undefined
          );

        const user = <LoginResponse>response.responseData;
        this.user.set(user);
        this.isUserDefined.set(AdaaHelper.isDefined(user));

        this.resetPasswordOptions.set([
          {
            value: this.verificationMethod.SMS,
            text: `${this._translateService.instant("login.text_message")} ${this._getEncodeMobileNumber(user)}`,
            icon: "fa-mobile-screen",
          },
        ]);

        if (isQuestionsAnswered)
          this.resetPasswordOptions.update((options) => [
            ...options,
            {
              value: this.verificationMethod.QUESTIONS,
              text: this._translateService.instant("login.text_questions"),
              icon: "fa-circle-question",
            },
          ]);
      },
      error: () => this._markUserAsUndefined(),
    });
  }

  /**
   * @description
   * @private
   */
  private _getSecurityQuestions(): void {
    this._loginService.getSecurityQuestions(this._token).subscribe({
      next: (response: MainResponseModel) => {
        const questions = <SecurityQuestion[]>response.responseData;
        this.questions.set(questions);
        this._getUserByToken(AdaaHelper.isDefined(questions) && questions.length > 0);
      },
      error: () => this._markUserAsUndefined(),
    });
  }

  /**
   * @description The reset token is invalid or the account could not be loaded,
   * so the form is replaced by the error screen instead of acting on a missing user.
   * @private
   */
  private _markUserAsUndefined(): void {
    this.user.set(undefined);
    this.isUserDefined.set(false);
  }

  /**
   * @description To encode the mobile number so we can display just the last three digits.
   * @param userInformation
   * @private
   */
  private _getEncodeMobileNumber(userInformation: LoginResponse | undefined): string {
    let encodedMobilePhone = "";
    if (userInformation) {
      if (userInformation.mobilePhone) {
        encodedMobilePhone = AdaaHelper.clone(userInformation.mobilePhone);
        const lastThreeDigits = encodedMobilePhone.slice(-3);
        const mobileNumberStars = encodedMobilePhone.replace(/\S/gi, "*");
        const substr = mobileNumberStars.length - 3;
        encodedMobilePhone =
          this._appService.language() === Language.English
            ? mobileNumberStars.slice(0, substr) + lastThreeDigits
            : lastThreeDigits + mobileNumberStars.slice(0, substr);
      }
    }
    return encodedMobilePhone;
  }

  /**
   * @description To lock the verification button from keep sending requests to
   * the API, the user will be able to resend the verification code in defined number.
   */
  private _lockVerificationButton(): void {
    this._clearLockInterval();
    this.lockCounter.set(this._lockDurationInSeconds);

    this._lockIntervalId = setInterval(() => {
      const remaining = this.lockCounter() - 1;
      this.lockCounter.set(remaining);

      if (remaining > 0) {
        return;
      }

      this._clearLockInterval();
    }, 1000);
  }

  private _clearLockInterval(): void {
    if (this._lockIntervalId === undefined) {
      return;
    }

    clearInterval(this._lockIntervalId);
    this._lockIntervalId = undefined;
  }

  /**
   * @description To get the form data for validate by questions
   * @param firstQuestion:string
   * @param secondQuestion:string
   * @private
   */
  private _getDataValidateByQuestions(firstQuestion: string, secondQuestion: string): SecurityQuestionAnswerModel {
    return {
      securityQuestions: [
        { userId: this.user()?.id, securityQuestionId: this.questions()[0].id, answer: firstQuestion },
        { userId: this.user()?.id, securityQuestionId: this.questions()[1].id, answer: secondQuestion },
      ],
    };
  }

  /**
   * @description To get the form data for validate by code
   * @param verificationCode:string
   * @private
   */
  private _getDataValidateByCode(verificationCode: string): VerificationRequestSubmit {
    return {
      userId: this.user()?.id,
      token: verificationCode,
      doNotAskAgain: false,
    };
  }

  /**
   * @description To get the form data for new password form
   * @param password:string
   * @private
   */
  private _getDataPassword(password: string): NewPassword {
    return {
      email: this.user()?.email,
      newPassword: password,
    };
  }
}
